import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { ArrowUpRight } from 'lucide-react'
import Counter from '@/components/motion/counter'
import SectionLabel from '@/components/generals/section-label'
import TransitionLink from '@/components/motion/transition-link'
import { gsap, MOTION_QUERIES, useGSAP } from '@/lib/motion'

type Token = string | { image: string }

// The studio introduction, written as one sentence that fills in word by word
// as it is read, with two small windows into the studio opening inside the text.
const TOKENS: Token[] = [
    "We're", "Fit", "Design,", "an", "independent", "team", "in", "London",
    { image: '/images/studio/desk.avif' },
    "and", "Gdańsk.", "We", "plan,", "design", "and", "build", "Webflow",
    { image: '/images/studio/call.avif' },
    "websites", "for", "growing", "businesses,", "and", "the", "people", "who",
    "plan", "your", "website", "are", "the", "people", "who", "build", "it."
]

const Statement: FC = (): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const textRef = useRef<HTMLParagraphElement>(null)

    useGSAP(() => {
        const text = textRef.current

        if (!text) return

        const mm = gsap.matchMedia()

        mm.add(MOTION_QUERIES.motion, () => {
            const timeline = gsap.timeline({
                scrollTrigger: {
                    trigger: text,
                    start: 'top 78%',
                    end: 'bottom 52%',
                    scrub: 0.6
                }
            })

            timeline
                .fromTo(text.querySelectorAll('[data-word]'), { opacity: 0.14 }, {
                    opacity: 1,
                    stagger: 0.08,
                    duration: 0.3,
                    ease: 'none'
                }, 0)
                .fromTo(text.querySelectorAll('[data-window]'), { clipPath: 'inset(0% 50% 0% 50%)' }, {
                    clipPath: 'inset(0% 0% 0% 0%)',
                    stagger: 0.9,
                    duration: 0.5,
                    ease: 'power2.out'
                }, 0.4)
        })

        return () => mm.revert()
    }, { scope: sectionRef })

    return (
        <section id='studio-intro' ref={sectionRef} data-scene='paper' className='px-5 py-28 md:px-8 md:py-44'>
            <div className='grid gap-10 lg:grid-cols-12'>
                <div className='lg:col-span-3'>
                    <SectionLabel index='01'>
                        Who we are
                    </SectionLabel>
                </div>

                <div className='lg:col-span-9'>
                    <p
                        ref={textRef}
                        className='font-heading text-[clamp(1.85rem,3.7vw,4.1rem)] font-[560] leading-[1.08] tracking-[-0.035em] [font-variation-settings:"wdth"_104]'
                    >
                        {TOKENS.map((token, index) => typeof token === 'string'
                            ? (
                                <span key={index}>
                                    <span data-word='' className='inline-block'>
                                        {token}
                                    </span>
                                    {' '}
                                </span>
                            )
                            : (
                                <span key={index}>
                                    <span
                                        data-window=''
                                        aria-hidden='true'
                                        className='relative inline-block h-[0.78em] w-[1.75em] overflow-hidden align-[-0.04em]'
                                    >
                                        <img src={token.image} alt='' loading='lazy' decoding='async' className='absolute inset-0 size-full object-cover' />
                                    </span>
                                    {' '}
                                </span>
                            ))}
                    </p>

                    <TransitionLink href='/studio' className='group mt-12 inline-flex items-center gap-3 font-medium'>
                        <span className='link-line'>
                            Meet the studio
                        </span>

                        <ArrowUpRight aria-hidden='true' className='size-4 transition-transform duration-500 ease-expo group-hover:-translate-y-0.5 group-hover:translate-x-0.5' />
                    </TransitionLink>
                </div>
            </div>

            <dl className='mt-24 grid grid-cols-2 border-t border-rule lg:mt-32 lg:grid-cols-4'>
                <div className='border-b border-r border-rule py-8 pr-6 lg:border-b-0'>
                    <dt className='type-label text-quiet'>
                        Websites designed and built
                    </dt>

                    <dd className='type-h1 mt-6 tabular-nums'>
                        <Counter to={150} suffix='+' />
                    </dd>
                </div>

                <div className='border-b border-rule py-8 pl-6 lg:border-b-0 lg:border-r lg:pr-6'>
                    <dt className='type-label text-quiet'>
                        Delivering since
                    </dt>

                    <dd className='type-h1 mt-6 tabular-nums'>
                        2012
                    </dd>
                </div>

                <div className='border-r border-rule py-8 pr-6 lg:pl-6'>
                    <dt className='type-label text-quiet'>
                        Person in-house team
                    </dt>

                    <dd className='type-h1 mt-6 tabular-nums'>
                        <Counter to={10} />
                    </dd>
                </div>

                <div className='py-8 pl-6'>
                    <dt className='type-label text-quiet'>
                        Awwwards honourable mentions
                    </dt>

                    <dd className='type-h1 mt-6 tabular-nums'>
                        <Counter to={5} suffix='+' />
                    </dd>
                </div>
            </dl>
        </section>
    )
}

export default Statement
